// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { getReferenceString } from '@medplum/core';
import type { Appointment, Encounter, Observation, Patient, Practitioner, Reference } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';

const NOTE_OBSERVATION_SYSTEM = 'https://polarissleep.com/fhir/observation-type';
const PATIENT_NOTE_CODE = 'patient-note';
const COORDINATOR_NOTE_SYSTEM = 'https://polarissleep.com/fhir/coordinator-note';
const COORDINATOR_PATIENT_NOTE_CODE = 'note-for-patient';
const LOCK_EXTENSION = `${COORDINATOR_NOTE_SYSTEM}/locked`;

/**
 * A patient-visible note left by a clinician on an Encounter chart, surfaced
 * for the patient dashboard. Each row corresponds to a single
 * `Observation` of code `patient-note` plus the related Encounter / Appointment
 * needed to display the date, time, and authoring provider in the modal.
 */
export interface CareTeamNote {
  id: string;
  observation: Observation;
  text: string;
  /** ISO timestamp from the Observation, falling back to Encounter / Appointment. */
  dateISO: string | undefined;
  /** Practitioner reference from Observation.performer; the modal's "Note from ..." line. */
  authorRef: Reference<Practitioner> | undefined;
  encounter: Encounter | undefined;
  appointment: Appointment | undefined;
}

export interface UseCareTeamNotesReturn {
  notes: CareTeamNote[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

/**
 * Loads the signed-in patient's care-team notes — every `patient-note`
 * Observation written on a clinician's encounter chart — sorted newest first.
 * The Encounter and its Appointment are eagerly hydrated so the row + modal
 * can show the appointment date/time without a second round-trip.
 */
export function useCareTeamNotes(): UseCareTeamNotesReturn {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;
  const [notes, setNotes] = useState<CareTeamNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchNotes = useCallback(async () => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const patientRef = getReferenceString(profile);
      const [clinicalNotes, coordinatorNotes] = await Promise.all([
        medplum.searchResources('Observation', {
          subject: patientRef,
          code: `${NOTE_OBSERVATION_SYSTEM}|${PATIENT_NOTE_CODE}`,
          _sort: '-_lastUpdated',
          _count: '100',
        }),
        medplum.searchResources('Observation', {
          subject: patientRef,
          code: `${COORDINATOR_NOTE_SYSTEM}|${COORDINATOR_PATIENT_NOTE_CODE}`,
          _sort: '-_lastUpdated',
          _count: '100',
        }),
      ]);
      const observations = coordinatorNotes.filter((observation) =>
        observation.extension?.some((extension) => extension.url === LOCK_EXTENSION && extension.valueBoolean === true)
      );
      observations.push(...clinicalNotes);

      // Hydrate the linked Encounter (and its Appointment) for each note. Cluster
      // requests so the lookup is at most one fetch per unique Encounter / Appointment.
      const encounterRefs = Array.from(
        new Set(observations.map((o: Observation) => o.encounter?.reference).filter(Boolean) as string[])
      );
      const encounterMap = new Map<string, Encounter>();
      await Promise.all(
        encounterRefs.map(async (ref) => {
          try {
            const enc = await medplum.readReference<Encounter>({ reference: ref });
            if (enc.id) {
              encounterMap.set(ref, enc);
            }
          } catch {
            // 403/404 — patient may not be able to read this Encounter; skip silently.
          }
        })
      );

      const appointmentRefs = Array.from(
        new Set(
          Array.from(encounterMap.values())
            .map((enc) => enc.appointment?.[0]?.reference)
            .filter(Boolean) as string[]
        )
      );
      const appointmentMap = new Map<string, Appointment>();
      await Promise.all(
        appointmentRefs.map(async (ref) => {
          try {
            const appt = await medplum.readReference<Appointment>({ reference: ref });
            if (appt.id) {
              appointmentMap.set(ref, appt);
            }
          } catch {
            // ignore
          }
        })
      );

      const enriched: CareTeamNote[] = observations
        .filter((obs: Observation) => Boolean(obs.id) && Boolean(obs.valueString?.trim()))
        .map((obs: Observation) => {
          const encRef = obs.encounter?.reference;
          const encounter = encRef ? encounterMap.get(encRef) : undefined;
          const apptRef = encounter?.appointment?.[0]?.reference;
          const appointment = apptRef ? appointmentMap.get(apptRef) : undefined;
          const dateISO =
            obs.effectiveDateTime ??
            obs.issued ??
            appointment?.start ??
            encounter?.period?.start ??
            obs.meta?.lastUpdated;
          const author = (obs.performer ?? []).find((ref) => ref.reference?.startsWith('Practitioner/')) as
            | Reference<Practitioner>
            | undefined;
          return {
            id: obs.id as string,
            observation: obs,
            text: obs.valueString ?? '',
            dateISO,
            authorRef: author,
            encounter,
            appointment,
          };
        });

      setNotes(enriched);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load care-team notes'));
    } finally {
      setLoading(false);
    }
  }, [medplum, profile]);

  useEffect(() => {
    fetchNotes().catch(console.error);
  }, [fetchNotes]);

  return { notes, loading, error, refresh: fetchNotes };
}
